import { useContext, useMemo, useCallback } from "react";
import { useNavigationUrl } from "../../hooks/useNavigationUrl";
import { ROUTES, emailIcon, phoneIcon, sideArrowFig } from "../../constant";
import { Messages } from "../../constant/messages/toastMessages";
import { StateContext } from "../../context/globalContext/context";
import { iGlobalContext } from "../../context/globalContext/interface";
import { ScreenTypes } from "../../enums";
import HelpButton from "../../component/help&Support/tripHelp/components/HelpButton";
import styles from "./tripHelp.module.scss";
import AppButton from "../../component/reusableComponent/button/button";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../constant/google-analytics/GAEvents";

const SUPPORT_EMAIL_KEY = "SUPPORT_EMAIL_ID";
const IOS_NATIVE_ELEMENT_ID = "iosNative";
const PHONE_BORDER_COLOR = "#FBB040";

interface AppConfigItem {
  key: string;
  value: string;
}

export default function TripHelp() {
  const { isMobile, setScreen, prevNav, setPrevNav, itineraryId, appConfig }: iGlobalContext = useContext(StateContext);

  const { navigateWithParams } = useNavigationUrl();

  // Memoize expensive computations
  const isIOSNativeApp = useMemo(() => {
    const nativeElement = document.getElementById(IOS_NATIVE_ELEMENT_ID) as HTMLInputElement | null;
    return nativeElement?.value === "true";
  }, []);

  const supportEmail = useMemo(() => {
    return appConfig?.find((ele: AppConfigItem) => ele?.key === SUPPORT_EMAIL_KEY)?.value ?? "";
  }, [appConfig]);

  const emailSubject = useMemo(() => {
    const baseSubject = Messages["trip.help.email.subject"].message;
    const itineraryInfo = prevNav === ROUTES.TripHelp ? `- Itinerary ID:${itineraryId}` : "";
    return `${baseSubject} ${itineraryInfo}`.trim();
  }, [prevNav, itineraryId]);

  // Memoize event handlers
  const handleBackClick = useCallback(() => {
    triggerGAEvent(GA_LABEL_ENUMS.need_help_back_click);

    const shouldNavigateToTripDetails = prevNav === ROUTES.TripHelp;

    if (isMobile) {
      navigateWithParams(shouldNavigateToTripDetails ? ROUTES.TripDetails : ROUTES.MyTrips);
    } else {
      setScreen(shouldNavigateToTripDetails ? ScreenTypes.TripDetails : "");
    }

    setPrevNav("");
  }, [prevNav, isMobile, navigateWithParams, setScreen, setPrevNav]);

  const handleEmailClick = useCallback(() => {
    triggerGAEvent(GA_LABEL_ENUMS.need_help_email_us_click);

    if (isIOSNativeApp && window?.parent) {
      const message = {
        action: "mailto",
        data: {
          email: supportEmail,
          subject: encodeURIComponent(emailSubject),
        },
      };
      window.parent.postMessage(JSON.stringify(message));
    } else {
      window.location.href = `mailto:${supportEmail}?subject=${encodeURIComponent(emailSubject)}`;
    }
  }, [isIOSNativeApp, supportEmail, emailSubject]);

  const handlePhoneClick = useCallback(() => {
    triggerGAEvent(GA_LABEL_ENUMS.need_help_phone_click);
  }, []);

  return (
    <div className={styles.main}>
      <div className={styles.subContainer}>
        <div className={styles.header}>
          <AppButton
            iconBtn={sideArrowFig}
            btnText=""
            onClick={handleBackClick}
            iconHeight={12}
            variant="transparent"
          />
        </div>
        <h1 className={styles.tripHeader}>{Messages["trip.help.header"].message}</h1>
        <HelpButton
          title={Messages["trip.help.emailId"].messageTitle}
          icon={emailIcon}
          onClick={handleEmailClick}
          email
        />

        <HelpButton
          onClick={handlePhoneClick}
          icon={phoneIcon}
          title={Messages["trip.help.phone.number"].message}
          secondaryPhone={Messages["trip.help.phone.number.secondary"].message}
          borderColor={PHONE_BORDER_COLOR}
          phoneCall
        />
      </div>
    </div>
  );
}
